"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.unitTestExports = exports.default = void 0;
var _utils = require("./utils");
var _reportError = _interopRequireDefault(require("./reportError"));
const getCacheParentObject = globalObject => {
  const keyPath = ['axpScriptSupplier', 'globalMemoAsyncCacheParent'];
  const cacheParent = (0, _utils.getIn)(globalObject, keyPath);
  if (!(0, _utils.isValidObject)(cacheParent)) {
    const newCacheParent = {};
    (0, _utils.setIn)(globalObject, keyPath, newCacheParent);
    return newCacheParent;
  }
  return cacheParent;
};

/**
 * Wraps the given asynchronous (Promise-returning) function with a global cache (on the window
 * object) to provide memoization. Calling this globalMemoAsync function will either (1) get a value
 * from the cache and return it as a resolved Promise; or (2) call the wrapped function and return
 * the Promise that function returned. If there's already a pending Promise for the same inputs, the
 * wrapped function won't be called and instead the pending Promise will be returned.
 * globalMemoAsync is called as follows:
 * @example
 * globalMemoAsync({
 *  globalObject: window,
 *  cacheName: 'my-cache-name',
 *  inputs: ['the-inputs', 'my-function', 'depends-on'],
 * })(() => {
 *  // The wrapped function's code goes here.
 * });
 * @param {Object} globalObject - The JavaScript global object, e.g. the `window` object when
 * running in the browser.
 * @param {String} cacheName - A unique name for this cache.
 * @param {Array} inputs - These values will form the cache key. They will be used directly as
 * object keys, so complex types may not work as expected. They will not actually be passed to the
 * wrapped function, the caller of globalMemoAsync is responsible for providing a function which
 * takes no arguments.
 * @param {Function} wrappedFn - The asynchronous (Promise-returning) function being wrapped, it
 * must not take any arguments.
 * @returns {Promise} - Returns a Promise that (1) immediately resolves on a successful cache hit;
 * or (2) calls the wrapped function and then returns the Promise that function returned.
 */
const globalMemoAsync = _ref => {
  let {
    globalObject,
    cacheName,
    inputs
  } = _ref;
  return wrappedFn => {
    return new Promise(resolve => {
      var _window, _window2;
      let currentError = '';
      const localeOnPage = ((_window = window) === null || _window === void 0 ? void 0 : _window.UCMPageLocale) || ((_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.scriptSupplierPageLocale);
      if (!(0, _utils.isValidString)(cacheName)) {
        currentError = 'Expected cacheName to be a non-empty string.';
        (0, _reportError.default)('cacheNameError', currentError, localeOnPage);
        throw new TypeError(currentError);
      }
      if (!Array.isArray(inputs)) {
        currentError = 'Expected inputs to be an array.';
        (0, _reportError.default)('invalidArrayError', currentError, localeOnPage);
        throw new TypeError(currentError);
      }
      const cacheParent = getCacheParentObject(globalObject);
      const cacheKeyArray = [cacheName, ...inputs];
      const cacheEntry = (0, _utils.getIn)(cacheParent, cacheKeyArray);
      if (cacheEntry !== null && cacheEntry !== void 0 && cacheEntry.hasValue) {
        resolve(cacheEntry.value);
        return;
      }
      if ((cacheEntry === null || cacheEntry === void 0 ? void 0 : cacheEntry.promise) instanceof Promise) {
        resolve(cacheEntry.promise);
        return;
      }
      const newPromise = wrappedFn().then(value => {
        (0, _utils.setIn)(cacheParent, cacheKeyArray, {
          hasValue: true,
          value
        });
        return value;
      }, reason => {
        (0, _utils.setIn)(cacheParent, cacheKeyArray, {});
        throw reason;
      });
      (0, _utils.setIn)(cacheParent, cacheKeyArray, {
        promise: newPromise
      });
      resolve(newPromise);
    });
  };
};
var _default = globalMemoAsync;
exports.default = _default;
const unitTestExports = {
  getCacheParentObject
};
exports.unitTestExports = unitTestExports;